import { useId, type JSX } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
} from '@/components/ui';
import { cn } from '@/lib/utils';
import { statusToneInteractiveFieldClasses } from '@/lib/statusTone';

const MONTH_OPTIONS = [
  { value: '01', label: 'Gennaio' },
  { value: '02', label: 'Febbraio' },
  { value: '03', label: 'Marzo' },
  { value: '04', label: 'Aprile' },
  { value: '05', label: 'Maggio' },
  { value: '06', label: 'Giugno' },
  { value: '07', label: 'Luglio' },
  { value: '08', label: 'Agosto' },
  { value: '09', label: 'Settembre' },
  { value: '10', label: 'Ottobre' },
  { value: '11', label: 'Novembre' },
  { value: '12', label: 'Dicembre' },
] as const;

function parseMonthYear(value: string | undefined | null): {
  month: string;
  year: string;
} {
  if (!value) return { month: '', year: '' };
  const trimmed = value.trim();
  if (!trimmed) return { month: '', year: '' };

  // ISO format: YYYY-MM or YYYY-MM-DD (allow partial year while typing)
  const isoMatch = trimmed.match(/^(\d{1,4})-(\d{2})/);
  if (isoMatch) {
    return { month: isoMatch[2], year: isoMatch[1] };
  }

  // Just a year (allow partial while typing)
  const yearOnlyMatch = trimmed.match(/^(\d{1,4})$/);
  if (yearOnlyMatch) {
    return { month: '', year: yearOnlyMatch[1] };
  }

  // Legacy MM/YYYY or MM-YYYY format
  const slashMatch = trimmed.match(/^(\d{2})[/-](\d{4})$/);
  if (slashMatch) {
    return { month: slashMatch[1], year: slashMatch[2] };
  }

  // Just a 2-digit month
  const monthOnlyMatch = trimmed.match(/^(\d{2})$/);
  if (monthOnlyMatch) {
    return { month: monthOnlyMatch[1], year: '' };
  }

  return { month: '', year: '' };
}

function composeMonthYear(month: string, year: string): string {
  const cleanedYear = year.trim();
  const cleanedMonth = month.trim();
  if (!cleanedMonth && !cleanedYear) return '';
  // Output ISO format YYYY-MM for consistency with schema
  // Preserve partial year input while typing
  if (!cleanedMonth) return cleanedYear;
  if (!cleanedYear) return cleanedMonth;
  return `${cleanedYear}-${cleanedMonth}`;
}

export function MonthYearField({
  id,
  value,
  onChange,
  disabled = false,
  placeholder,
  error,
}: {
  id?: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  error?: boolean;
}): JSX.Element {
  const fallbackId = useId();
  const resolvedId = id ?? fallbackId;
  const parsed = parseMonthYear(value);

  const errorClasses = error ? statusToneInteractiveFieldClasses('danger') : [];

  const handleMonthChange = (nextMonth: string) => {
    onChange?.(composeMonthYear(nextMonth, parsed.year));
  };

  const handleYearChange = (nextYear: string) => {
    onChange?.(composeMonthYear(parsed.month, nextYear));
  };

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3">
      <Select
        value={parsed.month || undefined}
        onValueChange={handleMonthChange}
        disabled={disabled}
      >
        <SelectTrigger id={`${resolvedId}-month`} className={cn(...errorClasses)}>
          <SelectValue placeholder="Mese" />
        </SelectTrigger>
        <SelectContent>
          {MONTH_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        id={resolvedId}
        inputMode="numeric"
        pattern="\\d{4}"
        maxLength={4}
        placeholder={placeholder ?? 'Anno'}
        value={parsed.year}
        disabled={disabled}
        onChange={(event) => {
          // Only allow numeric characters
          const nextYear = event.target.value.replace(/\D/g, '');
          handleYearChange(nextYear);
        }}
        className={cn(...errorClasses)}
      />
    </div>
  );
}
